// 🔥 ULTRA-CLEAN ProposalExportService - Eliminates ALL Formatting Artifacts
import { saveAs } from 'file-saver';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, Table, TableRow, TableCell, WidthType, AlignmentType, BorderStyle } from 'docx';

interface ProposalContent {
  coverLetter: string;
  demoLink?: string;
  technicalSections: Array<{
    title: string;
    content: string;
  }>;
  feedback: string;
  contractAnalysis: string;
  profileAnalysis: string;
  message: string;
  proposalType?: string;
  aiFeedback?: any;
}

interface Contract {
  title: string;
  agency: string;
  id: string;
  notice_id: string;
}

// Helper to detect if content is JSON
function isJsonContent(content: string): boolean {
  if (!content || typeof content !== 'string') return false;
  const trimmed = content.trim();
  return (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
         (trimmed.startsWith('[') && trimmed.endsWith(']'));
}

// Helper function to safely get a string value with fallback
function safeStr(value: any, fallback: string = ''): string {
  if (value === undefined || value === null) return fallback;
  return String(value);
}

// Helper function to format optional field with label
function formatField(label: string, value: any, suffix: string = ''): string | null {
  if (value === undefined || value === null || value === '') return null;
  return `${label}: ${value}${suffix}`;
}

// Cover-letter readable text plus an appended Live Demonstration link (top-level
// content_json.demoLink), so the demo URL rides into PDF/Word/text exports.
function coverLetterWithDemo(proposal: ProposalContent): string {
  const base = jsonToReadableText(proposal.coverLetter, 'Cover Letter');
  const url = (proposal.demoLink || '').trim();
  return url ? `${base}\n\nLIVE DEMONSTRATION\n${url}` : base;
}

// Convert JSON section content to readable text
export function jsonToReadableText(content: string, sectionTitle: string): string {
  if (!isJsonContent(content)) {
    return content;
  }

  try {
    const data = JSON.parse(content);
    const lines: string[] = [];

    // Handle different section types
    if (sectionTitle.toLowerCase().includes('technical')) {
      if (data.scopeOverview) lines.push(`SCOPE OVERVIEW\n\n${data.scopeOverview}\n`);
      if (data.technicalChallenges) lines.push(`TECHNICAL CHALLENGES\n\n${data.technicalChallenges}\n`);
      if (data.methodology) {
        lines.push(`METHODOLOGY\n\n${safeStr(data.methodology.overallApproach)}\n`);
        if (data.methodology.tasks && Array.isArray(data.methodology.tasks)) {
          data.methodology.tasks.forEach((task: any, i: number) => {
            lines.push(`\nTask ${i + 1}: ${safeStr(task.name, 'Unnamed Task')}`);
            if (task.approach) lines.push(`${task.approach}`);
            if (task.tools && Array.isArray(task.tools) && task.tools.length > 0) {
              lines.push(`Tools: ${task.tools.join(', ')}`);
            }
            if (task.deliverables && Array.isArray(task.deliverables) && task.deliverables.length > 0) {
              lines.push(`Deliverables: ${task.deliverables.join(', ')}`);
            }
          });
          lines.push('');
        }
      }
      if (data.toolsAndTechnologies && Array.isArray(data.toolsAndTechnologies)) {
        lines.push('\nTOOLS & TECHNOLOGIES\n');
        data.toolsAndTechnologies.forEach((tool: any) => {
          const parts = [safeStr(tool.category)];
          if (tool.technology) parts.push(tool.technology);
          if (tool.purpose) parts.push(tool.purpose);
          lines.push(`• ${parts.filter(p => p).join(' - ')}`);
        });
        lines.push('');
      }
      if (data.innovation) lines.push(`\nINNOVATION\n\n${data.innovation}\n`);
      if (data.qualityAssurance) lines.push(`\nQUALITY ASSURANCE\n\n${data.qualityAssurance}\n`);
    } else if (sectionTitle.toLowerCase().includes('management')) {
      if (data.corporateOrganization) lines.push(`CORPORATE ORGANIZATION\n\n${data.corporateOrganization}\n`);
      if (data.organizationChart) lines.push(`\nORGANIZATION STRUCTURE\n\n${data.organizationChart}\n`);
      if (data.keyRoles && Array.isArray(data.keyRoles)) {
        lines.push('\nKEY MANAGEMENT ROLES\n');
        data.keyRoles.forEach((role: any) => {
          const roleLine = role.name ? `${safeStr(role.role)} - ${role.name}` : safeStr(role.role);
          lines.push(`\n${roleLine}`);
          if (role.qualifications) lines.push(`Qualifications: ${role.qualifications}`);
          if (role.responsibilities && Array.isArray(role.responsibilities) && role.responsibilities.length > 0) {
            lines.push('Responsibilities:');
            role.responsibilities.forEach((r: string) => lines.push(`  • ${r}`));
          }
        });
        lines.push('');
      }
      if (data.communicationPlan) {
        const comms = data.communicationPlan;
        lines.push('\nCOMMUNICATION PLAN\n');
        if (comms.reportingStructure) lines.push(`${comms.reportingStructure}`);
        if (comms.meetingCadence) lines.push(`\nMeeting Cadence: ${comms.meetingCadence}`);
        if (comms.toolsAndMethods && Array.isArray(comms.toolsAndMethods) && comms.toolsAndMethods.length > 0) {
          lines.push(`Communication Tools: ${comms.toolsAndMethods.join(', ')}`);
        }
        lines.push('');
      }
      if (data.riskManagement && Array.isArray(data.riskManagement)) {
        lines.push('\nRISK MANAGEMENT\n');
        data.riskManagement.forEach((risk: any) => {
          const riskDetails = [];
          if (risk.likelihood) riskDetails.push(`Likelihood: ${risk.likelihood}`);
          if (risk.impact) riskDetails.push(`Impact: ${risk.impact}`);
          const detailStr = riskDetails.length > 0 ? ` (${riskDetails.join(', ')})` : '';
          lines.push(`• ${safeStr(risk.risk)}${detailStr}`);
          if (risk.mitigation) lines.push(`  Mitigation: ${risk.mitigation}`);
        });
        lines.push('');
      }
      if (data.transitionPlan) lines.push(`\nTRANSITION PLAN\n\n${data.transitionPlan}\n`);
    } else if (sectionTitle.toLowerCase().includes('personnel') || sectionTitle.toLowerCase().includes('key')) {
      if (data.summary) lines.push(`PERSONNEL SUMMARY\n\n${data.summary}\n`);
      if (data.keyPersonnel && Array.isArray(data.keyPersonnel)) {
        lines.push('\nKEY PERSONNEL\n');
        data.keyPersonnel.forEach((person: any) => {
          lines.push(`\n${safeStr(person.name, 'TBD')} - ${safeStr(person.role, 'Position TBD')}`);
          if (person.yearsExperience) lines.push(`Experience: ${person.yearsExperience}+ years`);
          if (person.clearance) lines.push(`Clearance: ${person.clearance}`);
          if (person.certifications && Array.isArray(person.certifications) && person.certifications.length > 0) {
            lines.push(`Certifications: ${person.certifications.join(', ')}`);
          }
          if (person.education && Array.isArray(person.education) && person.education.length > 0) {
            lines.push(`Education: ${person.education.join(', ')}`);
          }
          if (person.relevantExperience && Array.isArray(person.relevantExperience) && person.relevantExperience.length > 0) {
            lines.push('Relevant Experience:');
            person.relevantExperience.forEach((exp: any) => {
              const expParts = [safeStr(exp.position, 'Position')];
              if (exp.organization) expParts.push(`at ${exp.organization}`);
              if (exp.years) expParts.push(`(${exp.years})`);
              lines.push(`  • ${expParts.join(' ')}`);
            });
          }
        });
        lines.push('');
      }
      if (data.staffingPlan) {
        lines.push('\nSTAFFING PLAN\n');
        if (typeof data.staffingPlan === 'string') {
          lines.push(data.staffingPlan);
        } else if (data.staffingPlan.approach) {
          lines.push(data.staffingPlan.approach);
        }
        lines.push('');
      }
      if (data.recruitmentApproach) lines.push(`\nRECRUITMENT APPROACH\n\n${data.recruitmentApproach}\n`);
      if (data.substitutionProcess) lines.push(`\nSUBSTITUTION PROCESS\n\n${data.substitutionProcess}\n`);
    } else if (sectionTitle.toLowerCase().includes('pricing') || sectionTitle.toLowerCase().includes('cost')) {
      if (data.contractTypeRecommendation) {
        const ctr = data.contractTypeRecommendation;
        lines.push('CONTRACT TYPE RECOMMENDATION\n');
        if (ctr.recommended) lines.push(`Recommended Type: ${ctr.recommended}`);
        if (ctr.rationale) lines.push(`Rationale: ${ctr.rationale}`);
        lines.push('');
      }
      if (data.laborCategories && Array.isArray(data.laborCategories)) {
        lines.push('\nLABOR CATEGORIES\n');
        data.laborCategories.forEach((cat: any) => {
          // Build category header - only include equivalent if it exists
          const catName = safeStr(cat.category, 'Labor Category');
          const header = cat.equivalent ? `${catName} (${cat.equivalent})` : catName;
          lines.push(`\n• ${header}`);

          // Build details lines - only include fields that exist
          const details: string[] = [];
          if (cat.minExperience) details.push(`Experience: ${cat.minExperience}`);
          if (cat.education) details.push(`Education: ${cat.education}`);
          if (details.length > 0) lines.push(`  ${details.join(', ')}`);

          const rates: string[] = [];
          if (cat.hourlyRate) rates.push(`Hourly Rate: ${cat.hourlyRate}`);
          if (cat.loadedRate) rates.push(`Loaded Rate: ${cat.loadedRate}`);
          if (rates.length > 0) lines.push(`  ${rates.join(', ')}`);
        });
        lines.push('');
      }
      if (data.indirectRates && Array.isArray(data.indirectRates) && data.indirectRates.length > 0) {
        lines.push('\nINDIRECT RATES\n');
        data.indirectRates.forEach((rate: any) => {
          const rateParts = [safeStr(rate.category, 'Rate')];
          if (rate.rate) rateParts.push(`${rate.rate}`);
          if (rate.basis) rateParts.push(`(${rate.basis})`);
          lines.push(`• ${rateParts.join(' - ')}`);
        });
        lines.push('');
      }
      if (data.rateMethodology) lines.push(`\nRATE METHODOLOGY\n\n${data.rateMethodology}\n`);
      if (data.costBreakdown && Array.isArray(data.costBreakdown) && data.costBreakdown.length > 0) {
        lines.push('\nCOST BREAKDOWN\n');
        data.costBreakdown.forEach((item: any) => {
          const itemParts = [safeStr(item.element, 'Item')];
          if (item.method) itemParts.push(item.method);
          if (item.basis) itemParts.push(`(${item.basis})`);
          lines.push(`• ${itemParts.join(' - ')}`);
        });
        lines.push('');
      }
      if (data.valueProposition) {
        lines.push('\nVALUE PROPOSITION\n');
        if (data.valueProposition.efficiencyMeasures && Array.isArray(data.valueProposition.efficiencyMeasures) && data.valueProposition.efficiencyMeasures.length > 0) {
          lines.push('Efficiency Measures:');
          data.valueProposition.efficiencyMeasures.forEach((m: string) => lines.push(`  • ${m}`));
        }
        if (data.valueProposition.bestValueDetermination) {
          lines.push(`\n${data.valueProposition.bestValueDetermination}`);
        }
        lines.push('');
      }
    } else if (sectionTitle.toLowerCase().includes('compliance') || sectionTitle.toLowerCase().includes('far')) {
      if (data.farCompliance && Array.isArray(data.farCompliance)) {
        lines.push('FAR COMPLIANCE\n');
        data.farCompliance.forEach((item: any) => {
          const clauseParts = [safeStr(item.clause, 'Clause')];
          if (item.title) clauseParts.push(item.title);
          if (item.status) clauseParts.push(item.status.toUpperCase());
          lines.push(`• ${clauseParts.join(' - ')}`);
          if (item.notes) lines.push(`  Notes: ${item.notes}`);
        });
        lines.push('');
      }
      if (data.certifications && Array.isArray(data.certifications)) {
        lines.push('\nCERTIFICATIONS\n');
        data.certifications.forEach((cert: any) => {
          const certParts = [safeStr(cert.name, 'Certification')];
          if (cert.status) certParts.push(cert.status);
          if (cert.number) certParts.push(`(${cert.number})`);
          lines.push(`• ${certParts.join(' - ')}`);
        });
        lines.push('');
      }
      if (data.acknowledgments) lines.push(`\nACKNOWLEDGMENTS\n\n${data.acknowledgments}\n`);
    } else if (sectionTitle.toLowerCase().includes('past performance')) {
      if (data.summary) lines.push(`SUMMARY\n\n${data.summary}\n`);
      if (data.contracts && Array.isArray(data.contracts)) {
        lines.push('\nPAST PERFORMANCE CONTRACTS\n');
        data.contracts.forEach((contract: any) => {
          lines.push(`\n${safeStr(contract.name, 'Contract')}`);
          if (contract.client) lines.push(`Client: ${contract.client}`);
          const valueInfo: string[] = [];
          if (contract.value) valueInfo.push(`Value: ${contract.value}`);
          if (contract.period) valueInfo.push(`Period: ${contract.period}`);
          if (valueInfo.length > 0) lines.push(valueInfo.join(', '));
          if (contract.scopeOfWork) lines.push(`Scope: ${contract.scopeOfWork}`);
          if (contract.relevance) lines.push(`Relevance: ${contract.relevance}`);
          if (contract.achievements && Array.isArray(contract.achievements) && contract.achievements.length > 0) {
            lines.push('Achievements:');
            contract.achievements.forEach((a: string) => lines.push(`  • ${a}`));
          }
        });
        lines.push('');
      }
      if (data.relevanceSummary) lines.push(`\nRELEVANCE SUMMARY\n\n${data.relevanceSummary}\n`);
    } else if (sectionTitle.toLowerCase().includes('executive')) {
      if (data.valueProposition) lines.push(`VALUE PROPOSITION\n\n${data.valueProposition}\n`);
      if (data.understandingOfRequirements) lines.push(`\nUNDERSTANDING OF REQUIREMENTS\n\n${data.understandingOfRequirements}\n`);
      if (data.keyDiscriminators && Array.isArray(data.keyDiscriminators)) {
        lines.push('\nKEY DISCRIMINATORS\n');
        data.keyDiscriminators.forEach((disc: any, i: number) => {
          lines.push(`\n${i + 1}. ${safeStr(disc.title, 'Discriminator')}`);
          if (disc.description) lines.push(`   ${disc.description}`);
        });
        lines.push('');
      }
      if (data.technicalOverview) lines.push(`\nTECHNICAL OVERVIEW\n\n${data.technicalOverview}\n`);
      if (data.managementOverview) lines.push(`\nMANAGEMENT OVERVIEW\n\n${data.managementOverview}\n`);
      if (data.conclusion) lines.push(`\nCONCLUSION\n\n${data.conclusion}\n`);
    } else {
      // Generic handling - just extract string values
      for (const [key, value] of Object.entries(data)) {
        if (typeof value === 'string' && value.trim()) {
          const title = key.replace(/([A-Z])/g, ' $1').toUpperCase().trim();
          lines.push(`${title}\n\n${value}\n`);
        } else if (Array.isArray(value) && value.length > 0) {
          const title = key.replace(/([A-Z])/g, ' $1').toUpperCase().trim();
          lines.push(`\n${title}\n`);
          value.forEach((item: any) => {
            if (typeof item === 'string') {
              lines.push(`• ${item}`);
            } else if (typeof item === 'object' && item !== null) {
              // Try to extract meaningful text from object
              const itemStr = item.name || item.title || item.description || JSON.stringify(item);
              lines.push(`• ${itemStr}`);
            }
          });
          lines.push('');
        }
      }
    }

    return lines.join('\n') || content;
  } catch {
    return content;
  }
}

// Detect if this is an orchestrated proposal
function isOrchestratedProposal(proposal: ProposalContent): boolean {
  return proposal.proposalType === 'orchestrated' ||
         !!proposal.aiFeedback ||
         (proposal.technicalSections?.some(s =>
           s.title === 'Technical Approach' ||
           s.title === 'Management Approach' ||
           s.title === 'Key Personnel' ||
           s.title === 'Pricing & Cost Strategy' ||
           s.title === 'FAR/DFARS Compliance'
         ));
}

export class ProposalExportService {
  
  /**
   * 🔥 ULTRA-CLEAN Word Export - Zero Artifacts
   */
  static async exportToWord(proposal: ProposalContent, contract: Contract): Promise<void> {
    try {
      const isOrchestrated = isOrchestratedProposal(proposal);

      // Build document children dynamically based on proposal type
      const children: (Paragraph | Table)[] = [
        // Title Page
        new Paragraph({
          text: contract.title,
          heading: HeadingLevel.TITLE,
          alignment: AlignmentType.CENTER,
          spacing: { after: 400 }
        }),

        new Paragraph({
          children: [new TextRun({ text: "PROPOSAL RESPONSE", bold: true, size: 28 })],
          alignment: AlignmentType.CENTER,
          spacing: { after: 300 }
        }),

        new Paragraph({
          children: [
            new TextRun({ text: "Submitted to: ", bold: true }),
            new TextRun(contract.agency)
          ],
          alignment: AlignmentType.CENTER,
          spacing: { after: 200 }
        }),

        new Paragraph({
          children: [
            new TextRun({ text: "Date: ", bold: true }),
            new TextRun(new Date().toLocaleDateString())
          ],
          alignment: AlignmentType.CENTER,
          spacing: { after: 400 }
        }),

        // Page break
        new Paragraph({
          text: "",
          pageBreakBefore: true
        }),

        // Cover Letter / Executive Summary
        new Paragraph({
          text: "COVER LETTER",
          style: "Heading1",
          spacing: { before: 400, after: 200 }
        }),
        ...this.parseUltraCleanContent(coverLetterWithDemo(proposal)),
      ];

      // Add Contract Analysis and Profile Analysis only for non-orchestrated proposals
      if (!isOrchestrated && proposal.contractAnalysis) {
        children.push(
          new Paragraph({
            text: "",
            pageBreakBefore: true
          }),
          new Paragraph({
            text: "CONTRACT ANALYSIS",
            style: "Heading1",
            spacing: { before: 400, after: 200 }
          }),
          ...this.parseUltraCleanContent(proposal.contractAnalysis)
        );
      }

      if (!isOrchestrated && proposal.profileAnalysis) {
        children.push(
          new Paragraph({
            text: "",
            pageBreakBefore: true
          }),
          new Paragraph({
            text: "CAPABILITY ANALYSIS",
            style: "Heading1",
            spacing: { before: 400, after: 200 }
          }),
          ...this.parseUltraCleanContent(proposal.profileAnalysis)
        );
      }

      // Technical Sections - Each gets its own page
      proposal.technicalSections.forEach(section => {
        // Convert JSON content to readable text before processing
        const readableContent = jsonToReadableText(section.content, section.title);

        children.push(
          new Paragraph({
            text: "",
            pageBreakBefore: true
          }),
          new Paragraph({
            text: section.title.toUpperCase(),
            style: "Heading1",
            spacing: { before: 400, after: 200 }
          }),
          ...this.parseUltraCleanContent(readableContent)
        );
      });

      const doc = new Document({
        styles: {
          default: {
            document: {
              run: {
                font: "Calibri",
                size: 22 // 11pt
              }
            }
          },
          paragraphStyles: [
            {
              id: "Heading1",
              name: "Heading 1",
              basedOn: "Normal",
              next: "Normal",
              run: {
                size: 32, // 16pt
                bold: true,
                color: "2563EB"
              },
              paragraph: {
                spacing: { before: 400, after: 200 }
              }
            },
            {
              id: "Heading2",
              name: "Heading 2",
              basedOn: "Normal",
              next: "Normal",
              run: {
                size: 28, // 14pt
                bold: true,
                color: "1E40AF"
              },
              paragraph: {
                spacing: { before: 300, after: 150 }
              }
            },
            {
              id: "Heading3",
              name: "Heading 3",
              basedOn: "Normal",
              next: "Normal",
              run: {
                size: 24, // 12pt
                bold: true,
                color: "374151"
              },
              paragraph: {
                spacing: { before: 200, after: 100 }
              }
            },
            {
              id: "Heading4",
              name: "Heading 4",
              basedOn: "Normal",
              next: "Normal",
              run: {
                size: 22, // 11pt
                bold: true,
                color: "6B7280"
              },
              paragraph: {
                spacing: { before: 150, after: 75 }
              }
            }
          ]
        },
        sections: [{
          properties: {},
          children: children
        }]
      });

      const blob = await Packer.toBlob(doc);
      const fileName = `${this.sanitizeFilename(contract.title)}_proposal.docx`;
      saveAs(blob, fileName);

    } catch (error: any) {
      console.error('Word export error:', error);
      throw new Error(`Word export failed: ${error.message}`);
    }
  }

  /**
   * 🧹 ULTRA-CLEAN Content Parser - Eliminates ALL artifacts
   */
  private static parseUltraCleanContent(content: string): (Paragraph | Table)[] {
    if (!content) return [new Paragraph({ text: "Content not available." })];

    // STEP 1: Ultra-aggressive cleaning
    const ultraClean = this.ultraCleanText(content);
    const lines = ultraClean.split('\n');
    const elements: (Paragraph | Table)[] = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i].trim();
      
      if (!line) {
        i++;
        continue;
      }

      // Check for tables first
      if (line.includes('|')) {
        const tableLines = this.extractTableLines(lines, i);
        if (tableLines.length > 1) {
          const table = this.createWordTable(tableLines);
          if (table) {
            elements.push(table);
            i += tableLines.length;
            continue;
          }
        }
      }

      // Process individual line
      const paragraph = this.parseCleanLine(line);
      if (paragraph) {
        elements.push(paragraph);
      }
      
      i++;
    }

    return elements.length > 0 ? elements : [new Paragraph({ text: content })];
  }

  /**
   * 🔥 ULTRA Text Cleaner - Removes ALL formatting artifacts
   */
  private static ultraCleanText(text: string): string {
    if (!text) return '';
    
    let cleaned = text;
    
    // STEP 1: Remove duplicate patterns like "*text*text*"
    cleaned = cleaned.replace(/(\*[^*]+\*)\1+/g, '$1'); // Remove duplicates
    cleaned = cleaned.replace(/([^*]+)\1+/g, '$1'); // Remove text duplicates
    
    // STEP 2: Clean up markdown artifacts
    cleaned = cleaned.replace(/\*\*\*([^*]+)\*\*\*/g, '$1'); // ***text***
    cleaned = cleaned.replace(/\*\*([^*]+)\*\*/g, '$1'); // **text**
    cleaned = cleaned.replace(/\*([^*]+)\*/g, '$1'); // *text*
    cleaned = cleaned.replace(/___([^_]+)___/g, '$1'); // ___text___
    cleaned = cleaned.replace(/__([^_]+)__/g, '$1'); // __text__
    cleaned = cleaned.replace(/_([^_]+)_/g, '$1'); // _text_
    
    // STEP 3: Remove specific problematic patterns seen in your export
    cleaned = cleaned.replace(/\*([^*:]+):\*\*([^*:]+):\*\*/g, '$1: $2:'); // *Weight:**Weight:** -> Weight:
    cleaned = cleaned.replace(/\*([^*]+)\*\*([^*]+)\*\*/g, '$1 $2'); // *text**text** -> text text
    
    // STEP 4: Clean up headers
    cleaned = cleaned.replace(/^#{1,6}\s+/gm, ''); // Remove # headers but keep text
    
    // STEP 5: Clean bullet points and lists
    cleaned = cleaned.replace(/^[\s]*[-*+•]\s*/gm, '• '); // Normalize bullets
    cleaned = cleaned.replace(/^[\s]*\d+\.\s*/gm, ''); // Remove number markers, keep text
    
    // STEP 6: Remove code blocks and inline code
    cleaned = cleaned.replace(/```[\s\S]*?```/g, ''); // Code blocks
    cleaned = cleaned.replace(/`([^`]+)`/g, '$1'); // Inline code
    
    // STEP 7: Remove links but keep text
    cleaned = cleaned.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1'); // [text](url) -> text
    cleaned = cleaned.replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1'); // Images
    
    // STEP 8: Clean up remaining HTML-like tags
    cleaned = cleaned.replace(/<[^>]+>/g, ''); // Remove HTML tags
    
    // STEP 9: Remove blockquotes
    cleaned = cleaned.replace(/^>\s*/gm, '');
    
    // STEP 10: Clean up horizontal rules
    cleaned = cleaned.replace(/^[-*_]{3,}$/gm, '');
    
    // STEP 11: Aggressive whitespace cleanup
    cleaned = cleaned.replace(/\n\s*\n\s*\n/g, '\n\n'); // Max 2 line breaks
    cleaned = cleaned.replace(/[ \t]+/g, ' '); // Multiple spaces -> single space
    cleaned = cleaned.replace(/^\s+|\s+$/gm, ''); // Trim each line
    
    // STEP 12: Remove any remaining asterisks that aren't part of bullets
    cleaned = cleaned.replace(/(?<!^|\s)\*+(?!\s|$)/g, ''); // Standalone asterisks
    
    // STEP 13: Final cleanup - remove any duplicate colons or periods
    cleaned = cleaned.replace(/:+/g, ':'); // Multiple colons -> single
    cleaned = cleaned.replace(/\.{2,}/g, '.'); // Multiple periods -> single
    
    return cleaned.trim();
  }

  /**
   * 🎯 Clean Line Parser - Creates proper Word elements
   */
  private static parseCleanLine(line: string): Paragraph | null {
    const trimmed = line.trim();
    if (!trimmed) return null;

    // Check for indented content first (lines starting with spaces in original)
    const indentMatch = line.match(/^(\s+)/);
    const indentLevel = indentMatch ? Math.floor(indentMatch[1].length / 2) : 0;

    // Enhanced header detection
    if (this.isHeaderLine(trimmed)) {
      const cleanTitle = trimmed.replace(/[:\*#]+$/, '').trim(); // Only remove trailing : and *
      const level = this.detectHeaderLevel(trimmed);

      let style: string;
      switch (level) {
        case 1: style = "Heading1"; break;
        case 2: style = "Heading2"; break;
        case 3: style = "Heading3"; break;
        default: style = "Heading4";
      }

      return new Paragraph({
        text: cleanTitle,
        style: style,
        spacing: { before: level <= 2 ? 300 : 200, after: 100 }
      });
    }

    // Bullet points
    if (trimmed.startsWith('•')) {
      const text = trimmed.substring(1).trim();
      const runs = this.createFormattedRuns(text);

      return new Paragraph({
        children: [
          new TextRun({ text: "• " }),
          ...runs
        ],
        spacing: { after: 80 },
        indent: { left: 360 + (indentLevel * 180) }
      });
    }

    // Sub-bullet points (indented bullets)
    if (indentLevel > 0 && (trimmed.startsWith('-') || trimmed.startsWith('*'))) {
      const text = trimmed.substring(1).trim();
      const runs = this.createFormattedRuns(text);

      return new Paragraph({
        children: [
          new TextRun({ text: "  ○ " }),
          ...runs
        ],
        spacing: { after: 60 },
        indent: { left: 540 + (indentLevel * 180) }
      });
    }

    // Numbered lists (if any numbers remain)
    const numberMatch = trimmed.match(/^(\d+)\.\s*(.+)$/);
    if (numberMatch) {
      const [, number, text] = numberMatch;
      const runs = this.createFormattedRuns(text);

      return new Paragraph({
        children: [
          new TextRun({ text: `${number}. `, bold: true }),
          ...runs
        ],
        spacing: { after: 100 },
        indent: { left: 360 }
      });
    }

    // Check for labeled content like "Experience: 10+ years" or "Clearance: Secret"
    const labelMatch = trimmed.match(/^([A-Za-z][A-Za-z\s]+):\s*(.+)$/);
    if (labelMatch && labelMatch[1].length < 30) {
      const [, label, value] = labelMatch;
      return new Paragraph({
        children: [
          new TextRun({ text: `${label}: `, bold: true }),
          new TextRun(value)
        ],
        spacing: { after: 80 },
        indent: { left: indentLevel > 0 ? 360 + (indentLevel * 180) : 0 }
      });
    }

    // Regular paragraph
    const runs = this.createFormattedRuns(trimmed);
    return new Paragraph({
      children: runs,
      spacing: { after: 100 },
      alignment: AlignmentType.LEFT
    });
  }

  /**
   * 🏷️ Header Detection - Smart detection of what should be headers
   */
  private static isHeaderLine(text: string): boolean {
    // All caps and short - these are section headers
    if (text === text.toUpperCase() && text.length < 80 && text.length > 2) return true;

    // Common section header patterns followed by colon
    if (/^(SCOPE OVERVIEW|TECHNICAL CHALLENGES|METHODOLOGY|TOOLS & TECHNOLOGIES|INNOVATION|QUALITY ASSURANCE|CORPORATE ORGANIZATION|ORGANIZATION STRUCTURE|KEY MANAGEMENT ROLES|COMMUNICATION PLAN|RISK MANAGEMENT|TRANSITION PLAN|PERSONNEL SUMMARY|KEY PERSONNEL|STAFFING PLAN|RECRUITMENT APPROACH|SUBSTITUTION PROCESS|CONTRACT TYPE RECOMMENDATION|LABOR CATEGORIES|INDIRECT RATES|RATE METHODOLOGY|COST BREAKDOWN|VALUE PROPOSITION|FAR COMPLIANCE|CERTIFICATIONS|ACKNOWLEDGMENTS|SUMMARY|PAST PERFORMANCE CONTRACTS|RELEVANCE SUMMARY|UNDERSTANDING OF REQUIREMENTS|KEY DISCRIMINATORS|TECHNICAL OVERVIEW|MANAGEMENT OVERVIEW|CONCLUSION)/i.test(text)) return true;

    // Task headers
    if (/^Task\s+\d+:/i.test(text)) return true;

    return false;
  }

  /**
   * 📏 Header Level Detection
   */
  private static detectHeaderLevel(text: string): number {
    // Main section headers (all caps, short)
    if (text === text.toUpperCase() && text.length < 40) return 2;
    // Task headers
    if (/^Task\s+\d+:/i.test(text)) return 3;
    // Longer headers
    if (text === text.toUpperCase()) return 2;
    return 3;
  }

  /**
   * 🎨 Formatted Text Runs Creator - Handles labels and clean text
   */
  private static createFormattedRuns(text: string): TextRun[] {
    // Clean the text first
    const finalClean = text
      .replace(/\*+/g, '') // Remove any remaining asterisks
      .replace(/_+/g, '') // Remove any remaining underscores
      .replace(/#+/g, '') // Remove any remaining hashes
      .replace(/\s+/g, ' ') // Clean up spaces
      .trim();

    // Check for inline labels like "Tools: item1, item2"
    const inlineLabelMatch = finalClean.match(/^(Tools|Deliverables|Certifications|Education|Clearance|Experience|Qualifications|Mitigation):\s*(.+)$/i);
    if (inlineLabelMatch) {
      const [, label, value] = inlineLabelMatch;
      return [
        new TextRun({ text: `${label}: `, bold: true }),
        new TextRun(value)
      ];
    }

    return [new TextRun(finalClean)];
  }

  /**
   * 🎨 Clean Text Runs Creator - Ensures completely clean text (legacy)
   */
  private static createCleanRuns(text: string): TextRun[] {
    return this.createFormattedRuns(text);
  }

  /**
   * 📊 Table Handling (Enhanced)
   */
  private static extractTableLines(lines: string[], startIndex: number): string[] {
    const tableLines: string[] = [];
    
    for (let i = startIndex; i < lines.length; i++) {
      const line = lines[i].trim();
      if (line.includes('|')) {
        // Skip separator lines with dashes
        if (!line.match(/^[\s|:\-]+$/)) {
          tableLines.push(line);
        }
      } else {
        break;
      }
    }
    
    return tableLines;
  }

  private static createWordTable(tableLines: string[]): Table | null {
    if (tableLines.length === 0) return null;

    const rows = tableLines.map(line => {
      return line.split('|')
        .map(cell => this.ultraCleanText(cell))
        .filter(cell => cell.length > 0);
    });

    if (rows.length === 0 || rows[0].length === 0) return null;

    const tableRows = rows.map((row, rowIndex) => {
      const isHeader = rowIndex === 0;
      
      return new TableRow({
        children: row.map(cellText => 
          new TableCell({
            children: [
              new Paragraph({
                children: this.createCleanRuns(cellText),
                alignment: AlignmentType.LEFT
              })
            ],
            shading: isHeader ? { fill: "E5E7EB" } : undefined,
            borders: {
              top: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
              bottom: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
              left: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
              right: { style: BorderStyle.SINGLE, size: 1, color: "000000" }
            }
          })
        )
      });
    });

    return new Table({
      rows: tableRows,
      width: { size: 100, type: WidthType.PERCENTAGE }
    });
  }

  /**
   * PDF Export (HTML-based)
   */
  static async exportToPDF(proposal: ProposalContent, contract: Contract): Promise<void> {
    try {
      const htmlContent = this.generateUltraCleanHTML(proposal, contract);
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const fileName = `${this.sanitizeFilename(contract.title)}_proposal.html`;
      saveAs(blob, fileName);
      
      setTimeout(() => {
        alert('HTML file downloaded! Open it in your browser, then use Ctrl+P (Cmd+P on Mac) and select "Save as PDF" to create a PDF.');
      }, 500);
      
    } catch (error) {
      console.error('PDF export error:', error);
      throw new Error(`PDF export failed: ${error.message}`);
    }
  }

  /**
   * Ultra-Clean HTML Generator
   */
  private static generateUltraCleanHTML(proposal: ProposalContent, contract: Contract): string {
    const isOrchestrated = isOrchestratedProposal(proposal);

    // Build sections dynamically
    let sections = `
    <div class="section">
        <h1>Cover Letter</h1>
        ${this.formatUltraCleanHTML(coverLetterWithDemo(proposal))}
    </div>`;

    // Add Contract/Profile Analysis only for non-orchestrated
    if (!isOrchestrated && proposal.contractAnalysis) {
      sections += `
    <div class="section">
        <h1>Contract Analysis</h1>
        ${this.formatUltraCleanHTML(proposal.contractAnalysis)}
    </div>`;
    }

    if (!isOrchestrated && proposal.profileAnalysis) {
      sections += `
    <div class="section">
        <h1>Capability Analysis</h1>
        ${this.formatUltraCleanHTML(proposal.profileAnalysis)}
    </div>`;
    }

    // Add technical sections with JSON conversion
    sections += proposal.technicalSections.map(section => `
    <div class="section">
        <h1>${section.title}</h1>
        ${this.formatUltraCleanHTML(jsonToReadableText(section.content, section.title))}
    </div>
    `).join('');

    return `<!DOCTYPE html>
<html>
<head>
    <title>${contract.title} - Proposal</title>
    <style>
        @page { margin: 1in; }
        body { font-family: 'Times New Roman', serif; font-size: 12pt; line-height: 1.6; }
        h1 { color: #2563eb; border-bottom: 2px solid #2563eb; page-break-before: always; font-size: 18pt; }
        h2 { color: #1e40af; margin-top: 2em; font-size: 16pt; }
        h3 { color: #374151; margin-top: 1.5em; font-size: 14pt; }
        h4 { color: #6b7280; margin-top: 1em; font-size: 12pt; }
        .header { text-align: center; margin-bottom: 2em; }
        .section { margin-bottom: 2em; page-break-inside: avoid; }
        table { border-collapse: collapse; width: 100%; margin: 1em 0; }
        th, td { border: 1px solid #000; padding: 8px; text-align: left; }
        th { background-color: #f0f0f0; font-weight: bold; }
        ul, ol { margin: 1em 0; }
        li { margin: 0.5em 0; }
        p { margin: 0.5em 0; }
        @media print { .section { page-break-before: always; } }
    </style>
</head>
<body>
    <div class="header">
        <h1>${contract.title}</h1>
        <p><strong>Submitted to:</strong> ${contract.agency}</p>
        <p><strong>Date:</strong> ${new Date().toLocaleDateString()}</p>
        <p><strong>Contract ID:</strong> ${contract.notice_id}</p>
    </div>
    ${sections}
</body>
</html>`;
  }

  private static formatUltraCleanHTML(content: string): string {
    if (!content) return '<p>Content not available</p>';
    
    const cleaned = this.ultraCleanText(content);
    return cleaned
      .split('\n')
      .filter(line => line.trim())
      .map(line => {
        const trimmed = line.trim();
        
        if (this.isHeaderLine(trimmed)) {
          const level = this.detectHeaderLevel(trimmed);
          const cleanTitle = trimmed.replace(/[:\*#]+/g, '').trim();
          return `<h${level + 1}>${cleanTitle}</h${level + 1}>`;
        }
        
        if (trimmed.startsWith('•')) {
          return `<li>${trimmed.substring(1).trim()}</li>`;
        }
        
        return `<p>${trimmed}</p>`;
      })
      .join('');
  }

  /**
   * Other export methods (Markdown, Sections)
   */
  static async exportToMarkdown(proposal: ProposalContent, contract: Contract): Promise<void> {
    try {
      const isOrchestrated = isOrchestratedProposal(proposal);

      let markdown = `# ${contract.title}

**Submitted to:** ${contract.agency}
**Date:** ${new Date().toLocaleDateString()}
**Contract ID:** ${contract.notice_id}

---

## Cover Letter

${this.ultraCleanText(coverLetterWithDemo(proposal))}

---`;

      // Add Contract/Profile Analysis only for non-orchestrated
      if (!isOrchestrated && proposal.contractAnalysis) {
        markdown += `

## Contract Analysis

${this.ultraCleanText(proposal.contractAnalysis)}

---`;
      }

      if (!isOrchestrated && proposal.profileAnalysis) {
        markdown += `

## Capability Analysis

${this.ultraCleanText(proposal.profileAnalysis)}

---`;
      }

      // Add technical sections with JSON conversion
      markdown += '\n\n' + proposal.technicalSections.map(section => `## ${section.title}

${this.ultraCleanText(jsonToReadableText(section.content, section.title))}

---`).join('\n\n');

      const blob = new Blob([markdown], { type: 'text/markdown' });
      const fileName = `${this.sanitizeFilename(contract.title)}_proposal.md`;
      saveAs(blob, fileName);

    } catch (error: any) {
      console.error('Markdown export error:', error);
      throw new Error(`Markdown export failed: ${error.message}`);
    }
  }

  static async exportSections(proposal: ProposalContent, contract: Contract): Promise<void> {
    try {
      const isOrchestrated = isOrchestratedProposal(proposal);

      const sections: Array<{ name: string; content: string; title: string }> = [
        { name: 'Cover_Letter', content: proposal.coverLetter, title: 'Cover Letter' },
      ];

      // Add Contract/Profile Analysis only for non-orchestrated
      if (!isOrchestrated && proposal.contractAnalysis) {
        sections.push({ name: 'Contract_Analysis', content: proposal.contractAnalysis, title: 'Contract Analysis' });
      }
      if (!isOrchestrated && proposal.profileAnalysis) {
        sections.push({ name: 'Capability_Analysis', content: proposal.profileAnalysis, title: 'Capability Analysis' });
      }

      // Add technical sections
      proposal.technicalSections.forEach(section => {
        sections.push({
          name: this.sanitizeFilename(section.title),
          content: section.content,
          title: section.title
        });
      });

      for (const section of sections) {
        const readableContent = jsonToReadableText(section.content, section.title);
        const content = `# ${section.name.replace(/_/g, ' ')}\n\n${this.ultraCleanText(readableContent)}`;
        const blob = new Blob([content], { type: 'text/markdown' });
        const fileName = `${this.sanitizeFilename(contract.title)}_${section.name}.md`;
        saveAs(blob, fileName);

        await new Promise(resolve => setTimeout(resolve, 200));
      }

    } catch (error: any) {
      console.error('Section export error:', error);
      throw new Error(`Section export failed: ${error.message}`);
    }
  }

  private static sanitizeFilename(filename: string): string {
    return filename.replace(/[^a-z0-9]/gi, '_').substring(0, 50);
  }
}